import { put, takeLatest, call } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  PatientApi,
  Patient,
  PatientListResponse,
  PatientAnalyticsResponse,
  MedicalInfoUpdate,
} from '../../api/services/patients';
import {
  fetchPatients,
  fetchPatientsSuccess,
  fetchPatientsFailure,
  fetchPatientDetails,
  fetchPatientDetailsSuccess,
  fetchPatientDetailsFailure,
  createPatient,
  createPatientSuccess,
  createPatientFailure,
  updatePatient,
  updatePatientSuccess,
  updatePatientFailure,
  updateMedicalInfo,
  updateMedicalInfoSuccess,
  updateMedicalInfoFailure,
  deletePatient,
  deletePatientSuccess,
  deletePatientFailure,
  fetchPatientAnalytics,
  fetchPatientAnalyticsSuccess,
  fetchPatientAnalyticsFailure,
} from '../slices/patientsSlice';
import { PatientFormData } from '../../types/patient';
import { parseDjangoError, getErrorMessage, type ErrorResponse } from '../../utils/errorParser';

// Fetch all patients
function* fetchPatientsSaga() {
  try {
    const response: PatientListResponse | Patient[] = yield call(PatientApi.getAll);

    // Handle both paginated response with results field and direct array response
    if (Array.isArray(response)) {
      // Direct array response
      yield put(fetchPatientsSuccess(response));
    } else if (response && 'results' in response && response.results) {
      // Paginated response
      yield put(fetchPatientsSuccess(response.results));
    } else {
      console.error('Unexpected API response format:', response);
      yield put(fetchPatientsFailure('Failed to fetch patients data'));
    }
  } catch (error: any) {
    console.error('Error fetching patients:', error);
    const errorMessage = getErrorMessage(error);
    yield put(fetchPatientsFailure(errorMessage));
  }
}

function* fetchPatientDetailsSaga(action: PayloadAction<number | string>) {
  try {
    const response: PatientListResponse | Patient = yield call(PatientApi.getById, action.payload);
    
    // Handle both paginated response with results field and direct Patient response
    let patient: Patient | null = null;
    
    if (response && 'results' in response && Array.isArray(response.results)) {
      // Paginated response - take the first result
      patient = response.results.length > 0 ? response.results[0] : null;
    } else if (response && 'id' in response) {
      // Direct Patient response
      patient = response as Patient;
    }
    
    if (patient) {
      yield put(fetchPatientDetailsSuccess(patient));
    } else {
      yield put(fetchPatientDetailsFailure('Patient not found. Please check if the patient exists.'));
    }
  } catch (error: any) {
    console.error('Error fetching patient details:', error);
    const errorMessage = getErrorMessage(error);
    yield put(fetchPatientDetailsFailure(errorMessage || 'Unable to fetch patient details. Please try again later.'));
  }
}

// Create patient
function* createPatientSaga(
  action: PayloadAction<{ data: PatientFormData; cb: (patientId: string) => void }>
) {
  try {
    const patient = action.payload.data;
    let formattedDob = null;
    if (patient.dateOfBirth) {
      try {
        const date = new Date(patient.dateOfBirth);
        formattedDob = date.toISOString().split('T')[0]; // Gets YYYY-MM-DD format
      } catch (error) {
        console.error('Error formatting date of birth:', error);
      }
    }
    // Clean phone number by removing spaces
    const cleanPhone = patient.phone.replace(/\s/g, '');

    const backendPatient = {
      email: patient.email,
      first_name: patient.firstName,
      last_name: patient.lastName,
      address: patient.address,
      phone: cleanPhone,
      dob: formattedDob,
      gender: patient.gender,
      occupation: patient.occupation || '',
      referred_by: patient.referredBy || '',
      language: 'EN',
      communication_preference: 'EMAIL',
      conditions: patient.primaryCondition || '',
      medical_info: {
        medicare: patient.medicareNumber || '0',
        phone: cleanPhone,
        cur_medications: patient.medications || '',
        medical_history: patient.medicalHistory || '',
        prev_treatments: patient.previousTreatments || '',
        emergency_contact: patient.emergencyContactName || '',
        patient_relation: patient.emergencyContactRelation || '',
        patient_profile: '',
        emergency_phone: patient.emergencyContactPhone || '',
        insurance_provider: patient.insuranceProvider || '',
        insurance_number: patient.insuranceNumber || 0,
        insurance_group: patient.insuranceGroup || '',
        document_loc: '',
        allergies: patient.allergies || '',
      },
    };
    const response: { data: Patient; message?: string } = yield call(
      PatientApi.createPatient,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      backendPatient as any
    );
    if (response && response) {
      yield put(createPatientSuccess());
      // Refresh the patients list
      yield put(fetchPatients());
      console.log('response.data', response.data);

      if (action.payload.cb) {
        action.payload.cb(response.data?.id?.toString() || '');
      }
    } else {
      yield put(
        createPatientFailure({
          message: 'Failed to create patient. Please try again.',
        })
      );
    }
  } catch (error) {
    const parsedError = parseDjangoError(error as ErrorResponse);
    yield put(
      createPatientFailure({
        message: parsedError.message,
        fieldErrors: parsedError.fieldErrors,
        status: parsedError.status,
      })
    );
  }
}

// Update patient
function* updatePatientSaga(action: PayloadAction<{ id: number | string; data: PatientFormData }>) {
  try {
    const { id, data: formData } = action.payload;
    let formattedDob = formData.dateOfBirth;
    if (formData.dateOfBirth) {
      try {
        const date = new Date(formData.dateOfBirth);
        formattedDob = date.toISOString().split('T')[0]; // Gets YYYY-MM-DD format
      } catch (error) {
        console.error('Error formatting date of birth:', error);
      }
    }
    // Clean phone number by removing spaces
    const cleanPhone = formData.phone.replace(/\s/g, '');

    // Prepare update payload
    const updateData = {
      email: formData.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      address: formData.address,
      phone: cleanPhone,
      dob: formattedDob,
      occupation: formData.occupation,
      language: 'EN',
      communication_preference: 'EMAIL',
      conditions: formData.primaryCondition || '',
      medical_info: {
        medicare: formData.medicareNumber || 0,
        phone: cleanPhone,
        allergies: formData.allergies || '',
      },
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const response: PatientListResponse = yield call(PatientApi.updatePatient, id, updateData);
    if (response) {
      yield put(updatePatientSuccess());
      // Refresh patient details and list
      yield put(fetchPatientDetails(id));
      yield put(fetchPatients());
    } else {
      yield put(
        updatePatientFailure({
          message: 'Failed to update patient. Please try again.',
        })
      );
    }
  } catch (error) {
    const parsedError = parseDjangoError(error as ErrorResponse);
    yield put(
      updatePatientFailure({
        message: parsedError.message,
        fieldErrors: parsedError.fieldErrors,
        status: parsedError.status,
      })
    );
  }
}

// Update medical info
function* updateMedicalInfoSaga(
  action: PayloadAction<{ id: number | string; data: MedicalInfoUpdate }>
) {
  try {
    const { id, data: medicalInfo } = action.payload;

    const response: PatientListResponse = yield call(PatientApi.updateMedicalInfo, id, medicalInfo);
    if (response) {
      yield put(updateMedicalInfoSuccess());
      // Refresh patient details and list
      yield put(fetchPatientDetails(id));
      yield put(fetchPatients());
    } else {
      yield put(updateMedicalInfoFailure('Failed to update medical information'));
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(updateMedicalInfoFailure(errorMessage));
  }
}

// Delete patient
function* deletePatientSaga(action: PayloadAction<number | string>) {
  try {
    yield call(PatientApi.deletePatient, action.payload);
    yield put(deletePatientSuccess());
    // Refresh the patients list
    yield put(fetchPatients());
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(deletePatientFailure(errorMessage));
  }
}

// Fetch patient analytics
function* fetchPatientAnalyticsSaga() {
  try {
    const response: PatientAnalyticsResponse = yield call(PatientApi.getAnalytics);
    if (response && response.data) {
      yield put(fetchPatientAnalyticsSuccess(response.data));
    } else {
      yield put(fetchPatientAnalyticsFailure('Failed to fetch patient analytics'));
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(fetchPatientAnalyticsFailure(errorMessage));
  }
}

// Root saga combining all patients sagas
export function* watchPatientsActions() {
  yield takeLatest(fetchPatients.type, fetchPatientsSaga);
  yield takeLatest(fetchPatientDetails.type, fetchPatientDetailsSaga);
  yield takeLatest(createPatient.type, createPatientSaga);
  yield takeLatest(updatePatient.type, updatePatientSaga);
  yield takeLatest(updateMedicalInfo.type, updateMedicalInfoSaga);
  yield takeLatest(deletePatient.type, deletePatientSaga);
  yield takeLatest(fetchPatientAnalytics.type, fetchPatientAnalyticsSaga);
}
